import React, { useEffect, useId, useMemo, useState } from 'react';
import {
  buildFretboardMatrix,
  buildArpeggioNotes,
  build3NPSPositions,
  INLAY_FRETS,
  DOUBLE_INLAY_FRETS,
  isInActiveShape,
  STANDARD_TUNING,
  MODES,
} from '../../lib/theory';
import { useIsDesktop, useIsLandscapePhone } from '../../hooks/useIsDesktop';
import { lessonPatternPositions, spellBluesTone } from '../../lib/lesson-patterns';

// ── Layout (boards 18 + 20) ───────────────────────────────────────────────────
//
// The SVG scales to its container, so every number below is a viewBox unit.
//
// PHONE is authored 1:1 with board 18: rendered at the board's 358px width the
// viewBox scale is ~1, so strings land 28px apart and dots are 20px. Frets 0-12,
// equal-width frets (~26px each; the old taper crushed frets 8-12).
//
// DESKTOP is board 20: frets 0-15, equal-width frets (same as the phone),
// 36px dots on a 49.2px string gap. The board is 880 wide here so a typical
// main-area width (~900px) renders it near 1:1; it scales to fill any width.

const PHONE = {
  frets: 12,
  left: 16,          // strings start here; the string names sit in the 0-12 gutter
  boardWidth: 338,   // fret 0 slot through the end of fret 12
  gap: 28,
  pad: 12,
  nutW: 4,
  noteR: 10.5,       // outer radius (21px dot incl. ring) — 75% of the 28 string gap
  ring: 1.25,
  font: 9,
  fontSmall: 8,      // labels longer than 2 chars (e.g. "bb7")
  inlayR: 4,
  stringThick: [2.4, 2.1, 1.8, 1.5, 1.2, 1],
  nameX: 6,
  nameFont: 11,
  numFont: 11,
  numTop: 3,         // gap between the board bottom and the fret-number line
  numLine: 15,
  bottom: 20,        // room under the board for fret numbers
  marked: [3, 5, 7, 9, 12],
  viewLeft: 0,
  viewRightPad: 2,
};

// LANDSCAPE is a phone held sideways (useIsLandscapePhone): frets 0-15 like the desktop, equal
// frets (~52px) and a ~34px string gap (roomier than the phone, for bigger dots), so the whole
// neck fits in ~215px of height and the board needs no scrolling. Authored ~1:1 at a typical 812px-wide landscape screen.
const LANDSCAPE = {
  ...PHONE,
  frets: 15,
  boardWidth: 800,
  gap: 34,
  noteR: 13,         // 26px dot incl. ring, 76% of the 34 string gap
  font: 11,
  fontSmall: 10,
  inlayR: 4.5,
  nameFont: 12,
  numFont: 12,
  marked: [3, 5, 7, 9, 12, 15],
};

const DESKTOP = {
  frets: 15,
  left: 0,
  boardWidth: 880,
  boardHeight: 300,
  padFrac: 0.09,     // first/last string sit 9% in from the board edges
  nutW: 5,
  noteR: 18,
  ring: 1.25,
  font: 13,
  fontSmall: 11,
  inlayR: 5,
  stringThick: [3, 2.6, 2.2, 1.8, 1.4, 1.1],
  nameX: -11,
  nameFont: 13,
  numFont: 13,
  numTop: 10,
  numLine: 18,
  bottom: 30,
  marked: [3, 5, 7, 9, 12, 15],
  viewLeft: -22,     // gutter for the open-string names
  viewRightPad: 0,
};

/** How long the board takes to slide over for a lesson pattern past the last fret (ms). */
const LESSON_SLIDE_MS = 350;

/**
 * Slot widths (viewBox units) for fret 0..frets. Slot 0 is the open-string zone left of the nut.
 * Every slot is the same width, on every layout (Oct 2026): a real-neck taper squeezed the high
 * frets narrower than the dots on phones, and desktop was switched to match.
 */
function fretWidths(L, extra = 0) {
  return Array.from({ length: L.frets + 1 + extra }, () => L.boardWidth / (L.frets + 1));
}

function fretPositions(widths, left) {
  const positions = [left];
  for (let i = 1; i < widths.length; i++) {
    positions.push(positions[i - 1] + widths[i - 1]);
  }
  return positions;
}

// ── Formula translator ────────────────────────────────────────────────────────

/**
 * Translate Structure + Tonality → semitone interval formula.
 * Does NOT touch the theory engine — pure data mapping.
 */
function resolveFormula(structure, tonalityLower, modeName = 'Ionian') {
  if (structure === 'Modes')      return MODES[modeName] ?? MODES['Ionian'];
  const isMajor = tonalityLower === 'major';
  if (structure === 'Pentatonic') return isMajor ? [0, 2, 4, 7, 9]        : [0, 3, 5, 7, 10];
  // Full Scale (default)
  return isMajor ? [0, 2, 4, 5, 7, 9, 11] : [0, 2, 3, 5, 7, 8, 10];
}

/** Map display shape names → theory-engine shape names */
const SHAPE_NAME_MAP = {
  'C Shape': 'C SHAPE',
  'A Shape': 'A SHAPE',
  'G Shape': 'G SHAPE',
  'E Shape': 'E SHAPE',
  'D Shape': 'D SHAPE',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function Fretboard({
  guitarKey,
  guitarTonality,   // 'Major' | 'Minor'
  guitarStructure,  // 'Full Scale' | 'Pentatonic' | 'Modes' | 'Arpeggios'
  guitarMode,       // diatonic mode name, used when guitarStructure === 'Modes'
  guitarSystem,
  guitarShape,
  guitarStringFocus,
  guitarFilter,
  guitarLabel,      // 'None' | 'Note Names' | 'Intervals'
  tuning = STANDARD_TUNING,
  // Arpeggios-specific prop
  guitarArpeggioType,
  // Private lesson pattern (src/lib/lesson-patterns.js): the placed pattern, or null. App.js only
  // passes one while signed in, so without it the board is exactly the public board.
  guitarLessonPattern = null,
}) {
  const isDesktop = useIsDesktop();
  const isLandscape = useIsLandscapePhone();
  const L = isDesktop ? DESKTOP : isLandscape ? LANDSCAPE : PHONE;
  const DISPLAY_FRETS = L.frets;
  const numStrings    = tuning.length;
  const tonalityLower = guitarTonality.toLowerCase(); // 'major' | 'minor'

  // Translate Structure + Tonality → scale formula (not used for Arpeggios). A lesson pattern
  // brings its own (major, or blues: 1 b3 4 b5 5 b7).
  const lessonFormula = guitarLessonPattern ? guitarLessonPattern.formula : null;
  const formula = useMemo(
    () => lessonFormula ?? resolveFormula(guitarStructure, tonalityLower, guitarMode),
    [lessonFormula, guitarStructure, tonalityLower, guitarMode]
  );
  const isBluesLesson = guitarLessonPattern?.group === 'blues';

  // Lesson pattern positions ("string-fret"): the pattern once, at its lowest full position
  const lessonPositions = useMemo(
    () => (guitarLessonPattern ? lessonPatternPositions(guitarKey, guitarLessonPattern) : null),
    [guitarLessonPattern, guitarKey]
  );

  // A lesson pattern that runs past the last fret on screen (phone 12, landscape and desktop 15)
  // slides the board left by just enough frets to show all of it, hiding the first few. Only
  // lesson patterns do this; every other view keeps the nut on screen.
  const lessonShift = useMemo(() => {
    if (!lessonPositions || lessonPositions.size === 0) return 0;
    const top = Math.max(...[...lessonPositions].map((k) => Number(k.split('-')[1])));
    return Math.max(0, top - DISPLAY_FRETS);
  }, [lessonPositions, DISPLAY_FRETS]);

  // Frets drawn past the last one on screen, off to the right until the board slides. They
  // appear at once when a shift starts and leave only after the slide back has finished.
  const [extraFrets, setExtraFrets] = useState(lessonShift);
  useEffect(() => {
    if (lessonShift >= extraFrets) {
      if (lessonShift > extraFrets) setExtraFrets(lessonShift);
      return undefined;
    }
    const timer = setTimeout(() => setExtraFrets(lessonShift), LESSON_SLIDE_MS);
    return () => clearTimeout(timer);
  }, [lessonShift, extraFrets]);
  const RENDER_FRETS = DISPLAY_FRETS + Math.max(extraFrets, lessonShift);
  const clipId = `fretboard-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  // Derive CAGED activeShapes array: 'All Shapes' → [] (show all zones)
  const activeShapes = useMemo(() => {
    if (guitarShape === 'All Shapes') return [];
    return [SHAPE_NAME_MAP[guitarShape] ?? guitarShape];
  }, [guitarShape]);

  // Derive 3NPS positions Set when that system is active
  const npsPositions = useMemo(() => {
    if (guitarSystem !== '3 NPS') return null;
    const patternNumber = guitarShape === 'All Patterns'
      ? 0
      : parseInt(guitarShape.replace('Pattern ', ''), 10);
    return build3NPSPositions(guitarKey, formula, patternNumber, tuning);
  }, [guitarSystem, guitarShape, guitarKey, formula, tuning]);

  // Build note matrix (not used for Arpeggios)
  const matrix = useMemo(
    () => buildFretboardMatrix(guitarKey, formula, tuning, tonalityLower),
    [guitarKey, formula, tuning, tonalityLower]
  );

  // Build arpeggio nodes (Arpeggios mode only) — full-neck map of every chord tone
  const arpeggioNodes = useMemo(() => {
    if (guitarStructure !== 'Arpeggios') return null;
    return buildArpeggioNotes({
      key: guitarKey, type: guitarArpeggioType, tonality: guitarTonality,
      tuning, stringFocus: guitarStringFocus,
    });
  }, [guitarStructure, guitarKey, guitarArpeggioType, guitarTonality, tuning, guitarStringFocus]);

  // Geometry. Every slot is the same width, so a slide of N frets is N slot widths.
  const widths     = useMemo(() => fretWidths(L, RENDER_FRETS - DISPLAY_FRETS), [L, RENDER_FRETS, DISPLAY_FRETS]);
  const xPositions = useMemo(() => fretPositions(widths, L.left), [widths, L]);
  const right      = xPositions[DISPLAY_FRETS] + widths[DISPLAY_FRETS]; // end of the last fret on screen
  const drawnRight = xPositions[RENDER_FRETS] + widths[RENDER_FRETS]; // end of the last fret drawn
  const nutX       = xPositions[1];
  const slideX     = lessonShift * widths[0];

  const isPhone = L !== DESKTOP; // phone and landscape share the gap-based geometry
  const neckH   = isPhone ? L.pad * 2 + (numStrings - 1) * L.gap : L.boardHeight;
  const topPad  = isPhone ? L.pad : neckH * L.padFrac;
  const gap     = isPhone ? L.gap : (neckH - 2 * topPad) / (numStrings - 1);
  const midY    = neckH / 2;
  const viewW   = right + L.viewRightPad - L.viewLeft;
  const viewH   = neckH + L.bottom;

  const noteX   = (fretIdx) => xPositions[fretIdx] + widths[fretIdx] / 2;
  const stringY = (sIdx) => topPad + (numStrings - 1 - sIdx) * gap;

  // Visibility predicate (non-Arpeggios paths)
  const isNoteVisible = (cell) => {
    if (!cell.isInScale) return false;
    if (!guitarStringFocus[cell.stringIndex]) return false;

    if (guitarFilter === 'Triads Only') {
      // The blues scale's triad is minor, even with Major selected
      const triadIntervals = tonalityLower === 'minor' || isBluesLesson ? [0, 3, 7] : [0, 4, 7];
      if (!triadIntervals.includes(cell.interval)) return false;
    }
    if (guitarFilter === 'Roots Only' && !cell.isRoot) return false;

    // A lesson pattern replaces the CAGED window (App.js clears one when the other is picked)
    if (lessonPositions !== null) {
      if (!lessonPositions.has(`${cell.stringIndex}-${cell.fretIndex}`)) return false;
    } else if (guitarSystem === 'CAGED') {
      if (!isInActiveShape(cell.fretIndex, guitarKey, activeShapes)) return false;
    }
    if (guitarSystem === '3 NPS' && npsPositions !== null) {
      if (!npsPositions.has(`${cell.stringIndex}-${cell.fretIndex}`)) return false;
    }

    return true;
  };

  // Label text inside note dot (non-Arpeggios paths)
  const noteLabel = (cell) => {
    if (guitarLabel === 'Intervals')  return cell.scaleDegree || '';
    if (guitarLabel === 'Note Names') {
      // Blues spells its b5 as a flat (A blues: Eb), the same as the notes row
      return isBluesLesson ? spellBluesTone(guitarKey, cell.interval) : cell.noteName;
    }
    return '';
  };

  // One note dot: roots are solid terracotta with a white label; scale tones are
  // white with a sage ring and an ink label. Flat — the boards have no shadow.
  const renderDot = (key, fret, sIdx, isRoot, label) => {
    const cx = noteX(fret);
    const cy = stringY(sIdx);
    return (
      <g
        key={key}
        className="animate-note-spring"
        style={{ animationDelay: `${fret * 15}ms` }}
      >
        <circle
          cx={cx} cy={cy} r={L.noteR - L.ring / 2}
          strokeWidth={L.ring}
          style={{
            fill:   isRoot ? 'hsl(var(--root))' : 'hsl(var(--note-scale-fill))',
            stroke: isRoot ? 'hsl(var(--root))' : 'hsl(var(--sage))',
          }}
        />
        {label !== '' && label != null && (
          <text
            x={cx} y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={String(label).length > 2 ? L.fontSmall : L.font}
            fontWeight={800}
            className="pointer-events-none"
            style={{ fill: isRoot ? 'hsl(var(--primary-foreground))' : 'hsl(var(--ink))' }}
          >
            {label}
          </text>
        )}
      </g>
    );
  };

  // String name labels (left of nut). tuning[] is low→high (E,A,D,G,B,e for guitar).
  const openStringLabels = ['E', 'A', 'D', 'G', 'B', 'e'];

  const inlayFrets = INLAY_FRETS.filter(f => f <= RENDER_FRETS);

  return (
    <div className="w-full">
      <svg
        viewBox={`${L.viewLeft} 0 ${viewW} ${viewH}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        className="select-none"
      >
        {/* Everything right of the string names sits in a window that a lesson pattern past the
            last fret slides left (slideX); the names stay put. With no slide it changes nothing. */}
        <defs>
          <clipPath id={clipId}>
            {/* +1: the half of the closing fret wire that sits past `right` */}
            <rect x={L.left} y={0} width={right - L.left + 1} height={viewH} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
        <g
          className="transition-transform ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${-slideX}px)`, transitionDuration: `${LESSON_SLIDE_MS}ms` }}
        >
        {/* Board surface with hairline top/bottom edges */}
        <rect x={nutX} y={0} width={drawnRight - nutX} height={neckH} style={{ fill: 'hsl(var(--board))' }} />
        <rect x={nutX} y={0} width={drawnRight - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={nutX} y={neckH - 1} width={drawnRight - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />

        {/* Inlay dots */}
        {inlayFrets.map(f => {
          const cx = noteX(f);
          const dotStyle = { fill: 'hsl(var(--inlay))' };
          if (DOUBLE_INLAY_FRETS.includes(f)) {
            return (
              <g key={`inlay-${f}`}>
                <circle cx={cx} cy={midY - gap} r={L.inlayR} style={dotStyle} />
                <circle cx={cx} cy={midY + gap} r={L.inlayR} style={dotStyle} />
              </g>
            );
          }
          return <circle key={`inlay-${f}`} cx={cx} cy={midY} r={L.inlayR} style={dotStyle} />;
        })}

        {/* Nut — solid ink, flush against the left of fret 1 */}
        <rect x={nutX - L.nutW} y={0} width={L.nutW} height={neckH} style={{ fill: 'hsl(var(--string))' }} />

        {/* Fret wires — thin grey; the last one closes the end of the board */}
        {Array.from({ length: RENDER_FRETS - 1 }, (_, i) => i + 2).concat(RENDER_FRETS + 1).map(f => {
          const x = f > RENDER_FRETS ? drawnRight : xPositions[f];
          return (
            <rect
              key={`fret-${f}`}
              x={x - 0.5} y={0} width={1} height={neckH}
              style={{ fill: 'hsl(var(--fret))' }}
            />
          );
        })}

        {/* Strings — solid ink, thicker toward the low E; muted strings fade */}
        {tuning.map((_, s) => {
          const thick = L.stringThick[s] ?? 1.5;
          return (
            <rect
              key={`string-${s}`}
              x={L.left} y={stringY(s) - thick / 2}
              width={drawnRight - L.left} height={thick}
              style={{ fill: 'hsl(var(--string))' }}
              opacity={guitarStringFocus[s] ? 1 : 0.2}
            />
          );
        })}

        </g>
        </g>

        {/* Open-string names — left of the nut, clear of the open-string note dots */}
        {tuning.map((_, s) => (
          <text
            key={`string-label-${s}`}
            x={L.nameX}
            y={stringY(s)}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fill: 'hsl(var(--ink-muted))' }}
            fontSize={L.nameFont}
            fontWeight={700}
          >
            {openStringLabels[s]}
          </text>
        ))}

        <g clipPath={`url(#${clipId})`}>
        <g
          className="transition-transform ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${-slideX}px)`, transitionDuration: `${LESSON_SLIDE_MS}ms` }}
        >

        {/* ── Note markers ── */}

        {/* Arpeggios — full-neck map of every chord tone */}
        {guitarStructure === 'Arpeggios' && arpeggioNodes && (
          arpeggioNodes.map(node => {
            if (node.fret > DISPLAY_FRETS) return null;
            let label = '';
            if (guitarLabel === 'Note Names') label = node.noteName;
            else if (guitarLabel === 'Intervals') label = node.interval || '';
            return renderDot(`arp-${node.string}-${node.fret}`, node.fret, node.string, node.isRoot, label);
          })
        )}

        {/* Standard scale/mode note markers */}
        {guitarStructure !== 'Arpeggios' && matrix.map((row, s) =>
          row.map((cell) => {
            if (cell.fretIndex > RENDER_FRETS) return null;
            if (!isNoteVisible(cell)) return null;
            return renderDot(
              `guitar-note-${s}-${cell.fretIndex}-${guitarKey}-${guitarStructure}-${guitarTonality}`,
              cell.fretIndex, s, cell.isRoot, noteLabel(cell)
            );
          })
        )}

        {/* Fret numbers — under the board; inlay frets are bold ink, the rest muted */}
        {Array.from({ length: RENDER_FRETS + 1 }, (_, i) => i).map(f => {
          const marked = L.marked.includes(f) || (f > DISPLAY_FRETS && INLAY_FRETS.includes(f));
          return (
            <text
              key={`fnum-${f}`}
              x={noteX(f)} y={neckH + L.numTop + L.numLine / 2}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ fill: marked ? 'hsl(var(--ink))' : 'hsl(var(--ink-muted))' }}
              fontSize={L.numFont}
              fontWeight={marked ? 800 : 600}
              opacity={marked ? 1 : 0.75}
              className="select-none"
            >
              {f}
            </text>
          );
        })}
        </g>
        </g>
      </svg>

      {/* Empty-state message when Arpeggios yields no chord tones */}
      {guitarStructure === 'Arpeggios' && arpeggioNodes && arpeggioNodes.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-4">
          No arpeggio tones on this string set — enable more strings.
        </p>
      )}
    </div>
  );
}
